import type { CSSProperties } from 'react';
import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { tint } from '../../../helpers';

type CalWeekHeaderStyleProps = { fontWeight?: string | false };

type CalContainerStyleProps = { btnDiameter: number };

/**
 * Returns a raw CSS string (nested SCSS-style selectors) describing the
 * in-range day decorations. It is intentionally NOT a linaria/emotion tagged
 * template so it can be interpolated as raw CSS by both linaria (internally,
 * below) and by emotion consumers outside the design-system (e.g. the schedule
 * booking-site date navigator) that build fully dynamic per-day selectors.
 */
export const calendarDayInRangeStyles = ({
  rangeStartSelector,
  rangeEndSelector,
}: {
  rangeStartSelector: string;
  rangeEndSelector: string;
}) => `
  &:not(:first-of-type):not(:last-of-type):not(${rangeStartSelector}):not(${rangeEndSelector})::before {
    content: '';
    position: absolute;
    height: var(--circle-diameter);
    width: calc(var(--circle-diameter) + var(--space-between-buttons));
    top: 0;
    left: 0;
    z-index: ${theme.zIndex.background};
    background-color: var(--in-range-background);
  }

  &${rangeStartSelector}:not([aria-selected='true']),
  &${rangeEndSelector}:not([aria-selected='true']) {
    background-color: var(--in-range-background);
    border-radius: 50%;
  }

  &:first-of-type:not(:last-of-type):not(${rangeStartSelector}):not(${rangeEndSelector}) {
    background-color: var(--in-range-background);
    border-radius: 50%;

    &::before {
      width: calc(var(--circle-diameter) / 2 + var(--space-between-buttons));
      left: 50%;
      content: '';
      position: absolute;
      height: var(--circle-diameter);
      top: 0;
      z-index: ${theme.zIndex.background};
      background-color: var(--in-range-background);
    }
  }

  &${rangeStartSelector}:not(:last-of-type)::before {
    width: calc(var(--circle-diameter) / 2 + var(--space-between-buttons));
    left: 50%;
    content: '';
    position: absolute;
    height: var(--circle-diameter);
    top: 0;
    z-index: ${theme.zIndex.background};
    background-color: var(--in-range-background);
  }

  &:last-of-type:not(${rangeStartSelector}):not(${rangeEndSelector}) {
    background-color: var(--in-range-background);
    border-radius: 50%;

    &:not(:first-of-type)::before {
      width: calc(var(--circle-diameter) / 2 + var(--space-between-buttons));
      right: 50%;
      content: '';
      position: absolute;
      height: var(--circle-diameter);
      top: 0;
      z-index: ${theme.zIndex.background};
      background-color: var(--in-range-background);
    }
  }

  &${rangeEndSelector}:not(:first-of-type)::before {
    width: calc(var(--circle-diameter) / 2 + var(--space-between-buttons));
    right: 50%;
    content: '';
    position: absolute;
    height: var(--circle-diameter);
    top: 0;
    z-index: ${theme.zIndex.background};
    background-color: var(--in-range-background);
  }
`;

// Build-time evaluable constants (interpolated into the static linaria css below).
const spaceBetweenButtons = theme.spacing(1);
const inRangeBackground = theme.colors.neutral10 ?? tint(0.85, theme.colors.primary50);
const todayDisabledColor = tint(0.5, theme.colors.primary50);
const dayInRangeRules = calendarDayInRangeStyles({
  rangeStartSelector: "[range-start='true']",
  rangeEndSelector: "[range-end='true']",
});

export const noOutline = css`
  outline: none;
`;

export const calWeekHeaderStyle = css`
  color: ${theme.colors.neutral80};
  font-size: ${theme.fontSize(16)};
  margin-bottom: ${theme.spacing(1)};
`;

export const calWeekHeaderBoldStyle = css`
  font-weight: ${theme.font.weight.bold};
`;

/**
 * The day-button diameter and overall container width depend on the runtime
 * `btnDiameter` prop, so they are driven by CSS custom properties set inline on
 * the container (see the calendar display components):
 *   --cal-circle-diameter, --cal-container-width
 */
export const calContainerStyle = css`
  width: var(--cal-container-width);

  .calendar__months__years {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .cal__month-name {
    margin: 0;
  }

  .cal__day {
    height: var(--cal-circle-diameter);
    width: var(--cal-circle-diameter);
    border-radius: 50%;
    margin: calc(${spaceBetweenButtons} / 2);
  }

  button.cal__day {
    --circle-diameter: var(--cal-circle-diameter);
    --space-between-buttons: ${spaceBetweenButtons};
    --in-range-background: ${inRangeBackground};
    position: relative;

    &::after {
      content: '';
      width: 100%;
      height: 100%;
      display: block;
      border: 2px solid ${theme.colors.primary50};
      border-radius: 50%;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: opacity 150ms ease-out;
    }

    &[aria-disabled='true'] {
      color: ${theme.colors.neutral60};
      cursor: default;

      &::after {
        border-color: ${theme.colors.neutral5};
      }
    }

    &:focus {
      outline: none;
    }

    &:focus:not(:active)::after {
      opacity: 1;
    }

    &:hover:not([aria-disabled='true']) {
      /*Make sure our range-start and range-end don't override */
      background-color: ${theme.colors.neutral5} !important;
    }

    &[in-range='true']:hover {
      /*Make sure our range-start and range-end don't override */
      background-color: ${inRangeBackground} !important;
    }

    /*Selected day styles */
    &[aria-selected='true'] {
      color: ${theme.colors.white};
      background-color: ${theme.colors.primary50};

      &:hover,
      &:focus {
        /*Make sure our range-start and range-end don't override */
        background-color: ${theme.colors.neutral10} !important;
        ${theme.colors.neutral90 && `color: ${theme.colors.neutral90} !important;`}
      }
    }

    &[data-is-today='true']:not([aria-selected='true']) {
      color: ${theme.colors.primary50};
      font-weight: ${theme.font.weight.bold};

      &[aria-disabled='true'] {
        color: ${todayDisabledColor};
      }
    }

    &[in-range='true'] {
      ${dayInRangeRules}
    }
  }

  button.cal__months {
    text-align: center;
    flex: 0 0 30%;
    padding: ${theme.spacing(1.5)} 0;
    border-radius: ${theme.borderRadius.small};
    outline: none;
    position: relative;

    &::after {
      content: '';
      width: calc(100% + ${theme.spacing(1.5)});
      height: calc(100% + ${theme.spacing(1.5)});
      display: block;
      border: 2px solid ${theme.colors.primary50};
      border-radius: ${theme.borderRadius.small};
      opacity: 0;
      pointer-events: none;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: opacity 150ms ease-out;
    }

    &:not(:nth-last-of-type(-n + 3)) {
      margin-bottom: ${theme.spacing(1.5)};
    }

    &[data-is-today='true']:not([aria-selected='true']) {
      color: ${theme.colors.primary50};
      font-weight: ${theme.font.weight.bold};

      &[aria-disabled='true'] {
        color: ${todayDisabledColor};
      }
    }

    &[aria-disabled='true'] {
      color: ${theme.colors.neutral60};
      cursor: default;

      &::after {
        border-color: ${theme.colors.neutral5};
      }
    }

    &[aria-selected='true'] {
      color: ${theme.colors.white};
      background-color: ${theme.colors.primary50};

      &:hover,
      &:focus {
        background-color: ${theme.colors.neutral10};
        ${theme.colors.neutral90 && `color: ${theme.colors.neutral90};`}
      }
    }

    &:hover:not([aria-disabled='true']) {
      background-color: ${theme.colors.neutral5};
      transition: background-color 0.2s ease;
    }

    &:focus {
      transition: fill 250ms ease-in-out;
      &::after {
        opacity: 1;
      }
    }
  }
  .calendar__single {
    & button {
      padding: ${theme.spacing(2.5)} 0;
    }
  }
`;

/**
 * Runtime CSS custom property values for a given day-button diameter. Set these
 * inline on the calendar container so the static `calContainerStyle` above can
 * resolve its sizing.
 */
export const calContainerVars = ({ btnDiameter }: CalContainerStyleProps) =>
  ({
    '--cal-circle-diameter': theme.spacing(btnDiameter),
    '--cal-container-width': `${7 * (btnDiameter * 8 + 8)}px`,
  } as CSSProperties);

export const CalendarCommonStyles = {
  calWeekHeaderStyles: (props?: CalWeekHeaderStyleProps) =>
    props?.fontWeight ? cx(calWeekHeaderStyle, calWeekHeaderBoldStyle) : calWeekHeaderStyle,
  calContainerStyles: () => calContainerStyle,
  calContainerVars: (props?: CalContainerStyleProps) => calContainerVars({ btnDiameter: props?.btnDiameter || 4 }),
};
